/**
 * style-membership.js (Bold Redesign)
 * Renders three cascading membership tier panels from Admin-managed
 * metaobject data. Each tier's feature list is automatically prefixed
 * with "Everything in [previous tier], plus" based on tier_rank, so
 * the cascade always reflects reality without manually re-copying
 * lower-tier perks into every tier's content.
 *
 * NOTE: STORAGE_KEY must match exactly the key style-analysis.js uses
 * when it saves the analysis result — confirm this against the actual
 * save call in style-analysis.js.
 */

const STORAGE_KEY = 'ma_style_analysis';

function getRecommendedTier() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const tier = parsed?.analysis?.recommended_tier || parsed?.recommended_tier;
    return tier ? String(tier).toLowerCase() : null;
  } catch {
    return null;
  }
}

function loadTiers() {
  const dataEl = document.getElementById('ma-membership-tiers-data');
  if (!dataEl) return [];
  try {
    const tiers = JSON.parse(dataEl.textContent);
    return tiers.sort((a, b) => (a.rank || 0) - (b.rank || 0));
  } catch (err) {
    console.error('[style-membership] Failed to parse tier data:', err);
    return [];
  }
}

function renderMedia(tier) {
  if (tier.mediaVideoUrl) {
    return `
      <video class="ma-tier__media" autoplay muted loop playsinline
        poster="${tier.mediaImage || ''}">
        <source src="${tier.mediaVideoUrl}" type="video/mp4">
      </video>
    `;
  }
  if (tier.mediaImage) {
    return `<img class="ma-tier__media" src="${tier.mediaImage}" alt="${tier.name || ''}" loading="lazy">`;
  }
  return '<div class="ma-tier__media ma-tier__media--empty"></div>';
}

function renderStatRow(tier) {
  const stats = [];
  if (tier.discountPercent) stats.push({ label: 'Member Discount', value: tier.discountPercent });
  if (tier.eligibleProductsPercent) stats.push({ label: 'Catalog Access', value: tier.eligibleProductsPercent });
  if (tier.shippingNote) stats.push({ label: 'Shipping', value: tier.shippingNote });

  if (stats.length === 0) return '';

  return `
    <div class="ma-tier__stats">
      ${stats.map(s => `
        <div class="ma-tier__stat">
          <span class="ma-tier__stat-value">${s.value}</span>
          <span class="ma-tier__stat-label">${s.label}</span>
        </div>
      `).join('')}
    </div>
  `;
}

function renderFeatureList(tier, allTiers) {
  const lowerTier = allTiers
    .filter(t => t.rank < tier.rank)
    .sort((a, b) => b.rank - a.rank)[0]; // the tier immediately below this one

  const cascadeHeader = lowerTier
    ? `<p class="ma-tier__cascade">Everything in ${lowerTier.name}, plus:</p>`
    : '';

  const ownFeatures = (tier.features || []).filter(Boolean);

  return `
    ${cascadeHeader}
    <ul class="ma-tier__features">
      ${ownFeatures.map(f => `
        <li class="ma-tier__feature">
          <span class="ma-tier__feature-mark">&#10022;</span>
          <span>${f}</span>
        </li>
      `).join('')}
    </ul>
  `;
}

function renderTierCard(tier, allTiers, isRecommended) {
  const card = document.createElement('div');
  card.className = `ma-tier ${isRecommended ? 'is-featured' : ''}`;
  card.dataset.tierId = tier.id || '';

  card.innerHTML = `
    ${renderMedia(tier)}
    <div class="ma-tier__body">
      ${isRecommended
        ? '<span class="ma-tier__badge ma-tier__badge--recommended">Recommended For You</span>'
        : tier.badgeText ? `<span class="ma-tier__badge">${tier.badgeText}</span>` : ''}
      <h2 class="ma-tier__name">${tier.name || ''}</h2>
      ${tier.tagline ? `<p class="ma-tier__tagline">${tier.tagline}</p>` : ''}
      <p class="ma-tier__price">${tier.priceDisplay || 'Pricing coming soon'}</p>
      ${tier.intro ? `<p class="ma-tier__intro">${tier.intro}</p>` : ''}
      ${renderStatRow(tier)}
      ${renderFeatureList(tier, allTiers)}
      <a href="${buildSubscribeUrl(tier)}" class="ma-tier__cta">
        Select ${tier.name || 'This Tier'}
      </a>
    </div>
  `;

  return card;
}

function buildSubscribeUrl(tier) {
  const base = window.MA_MEMBERSHIP_SERVICE_URL || '';
  const normalizedTierId = (tier.id || '').trim().toLowerCase();
  const params = new URLSearchParams({ tier: normalizedTierId });
  if (window.MA_CUSTOMER_EMAIL) {
    params.set('email', window.MA_CUSTOMER_EMAIL);
  }
  return `${base}/subscribe?${params.toString()}`;
}

function init() {
  const root = document.getElementById('style-membership-root');
  if (!root) return;

  const tiers = loadTiers();

  if (tiers.length === 0) {
    root.innerHTML = '<p class="ma-membership__empty">Membership tiers are being finalized — check back soon.</p>';
    return;
  }

  const recommended = getRecommendedTier();

  tiers.forEach((tier) => {
    const isRecommended = recommended && tier.id && tier.id.toLowerCase() === recommended;
    root.appendChild(renderTierCard(tier, tiers, isRecommended));
  });
}

document.addEventListener('DOMContentLoaded', init);